export const agentQuery = (start = 0, limit = 10, baseFilter = {}, searchMatch = null) => {
    return [
        // Apply baseFilter
        baseFilter,
        // Optional search match stage
        ...(searchMatch ? [searchMatch] : []),

        {
            $facet: {
                results: [
                    { $sort: { createdAt: -1 } },
                    { $skip: start },
                    { $limit: limit },

                    {
                        $addFields: {
                            administrative_ids: {
                                $reduce: {
                                    input: '$admin_level',
                                    initialValue: [],
                                    in: {
                                        $cond: [
                                            { $eq: ['$$this.model', 'administrative'] },
                                            { $concatArrays: ['$$value', '$$this.data'] },
                                            '$$value'
                                        ]
                                    }
                                }
                            },
                            constituency_ids: {
                                $reduce: {
                                    input: '$admin_level',
                                    initialValue: [],
                                    in: {
                                        $cond: [
                                            { $eq: ['$$this.model', 'constituency'] },
                                            { $concatArrays: ['$$value', '$$this.data'] },
                                            '$$value'
                                        ]
                                    }
                                }
                            },
                            ward_ids: {
                                $reduce: {
                                    input: '$admin_level',
                                    initialValue: [],
                                    in: {
                                        $cond: [
                                            { $eq: ['$$this.model', 'ward'] },
                                            { $concatArrays: ['$$value', '$$this.data'] },
                                            '$$value'
                                        ]
                                    }
                                }
                            },
                            polling_ids: {
                                $reduce: {
                                    input: '$admin_level',
                                    initialValue: [],
                                    in: {
                                        $cond: [
                                            { $eq: ['$$this.model', 'polling'] },
                                            { $concatArrays: ['$$value', '$$this.data'] },
                                            '$$value'
                                        ]
                                    }
                                }
                            }
                        }
                    },

                    { $lookup: { from: 'administratives', localField: 'administrative_ids', foreignField: '_id', as: 'administratives' } },
                    { $lookup: { from: 'constituencies',   localField: 'constituency_ids',   foreignField: '_id', as: 'constituencies'   } },
                    { $lookup: { from: 'wards',            localField: 'ward_ids',           foreignField: '_id', as: 'wards'            } },
                    { $lookup: { from: 'pollings',         localField: 'polling_ids',        foreignField: '_id', as: 'pollings'         } },
                    {
                        $lookup: {
                            from: 'admins',
                            let: { issuedId: '$verified.issued' },
                            pipeline: [
                                {
                                    $match: {
                                        $expr: { $eq: ['$_id', '$$issuedId'] }
                                    }
                                },
                                { $project: { _id: 1, name: 1, username: 1, mobile_number: 1 } }
                            ],
                            as: 'verified_issued'
                        }
                    },
                    {
                        $addFields: {
                            'verified.issued': { $arrayElemAt: ['$verified_issued', 0] }
                        }
                    },
                    {
                        $lookup: {
                            from: 'agents',
                            let: { docId: '$meta.doc', mdl: '$meta.model' },
                            pipeline: [
                                {
                                    $match: {
                                        $expr: {
                                            $and: [
                                                { $eq: ['$_id', '$$docId'] },
                                                { $eq: ['$$mdl', 'agent'] }
                                            ]
                                        }
                                    }
                                },
                                { $project: { _id: 1, name: 1, username: 1, mobile_number: 1 } }
                            ],
                            as: 'meta_doc'
                        }
                    },

                    {
                        $addFields: {
                            'meta.doc': { $arrayElemAt: ['$meta_doc', 0] }
                        }
                    },

                    {
                        $addFields: {
                            administratives: { $map: { input: '$administratives', as: 'd', in: { _id: '$$d._id', name: '$$d.name' } } },
                            constituencies:  { $map: { input: '$constituencies',  as: 'd', in: { _id: '$$d._id', name: '$$d.name' } } },
                            wards:           { $map: { input: '$wards',           as: 'd', in: { _id: '$$d._id', name: '$$d.name' } } },
                            pollings:        { $map: { input: '$pollings',        as: 'd', in: { _id: '$$d._id', name: '$$d.name' } } }
                        }
                    },

                    {
                        $addFields: {
                            admin_level: [
                                { model: 'administrative', data: '$administratives' },
                                { model: 'constituency',   data: '$constituencies'  },
                                { model: 'ward',           data: '$wards'          },
                                { model: 'polling',        data: '$pollings'       }
                            ]
                        }
                    },

                    {
                        $project: {
                            password: 0,
                            administrative_ids: 0,
                            constituency_ids: 0,
                            ward_ids: 0,
                            polling_ids: 0,
                            administratives: 0,
                            constituencies: 0,
                            wards: 0,
                            pollings: 0,
                            meta_doc: 0,
                            verified_issued: 0
                        }
                    }
                ],
                totalCount: [
                    { $count: 'count' }
                ]
            }
        }
    ];
};

export const membershipQuery = (start = 0, limit = 10, baseFilter = {}, searchTerm = '', searchableFields = []) => {
    const needsFullName = searchableFields.some(f => f.field === 'full_name');

    const searchRegexMatch = searchTerm.trim() && searchableFields.length
        ? {
            $match: {
                $or: searchableFields.map(field =>
                    field.isNumeric
                        ? {
                            [field.field]: isNaN(Number(searchTerm)) ? -1 : Number(searchTerm)
                        }
                        : {
                            [field.field]: {
                                $regex: searchTerm,
                                $options: 'i'
                            }
                        }
                )
            }
        }
        : null;

    return [
        baseFilter,
        {
            $facet: {
                results: [
                    { $sort: { _id: -1 } },
                    { $skip: start },
                    { $limit: limit },

                    // Lookups
                    {
                        $lookup: {
                            from: "agents",
                            localField: "doc",
                            foreignField: "_id",
                            as: "agent"
                        }
                    },
                    { $unwind: { path: "$agent", preserveNullAndEmptyArrays: true } },

                    {
                        $lookup: {
                            from: "pollings",
                            localField: "polling_station",
                            foreignField: "_id",
                            as: "polling_station"
                        }
                    },
                    { $unwind: { path: "$polling_station", preserveNullAndEmptyArrays: true } },

                    {
                        $lookup: {
                            from: "administratives",
                            localField: "admin_level.provincial",
                            foreignField: "_id",
                            as: "admin_level.provincial"
                        }
                    },
                    { $unwind: { path: "$admin_level.provincial", preserveNullAndEmptyArrays: true } },

                    {
                        $lookup: {
                            from: "districts",
                            localField: "admin_level.district",
                            foreignField: "_id",
                            as: "admin_level.district"
                        }
                    },
                    { $unwind: { path: "$admin_level.district", preserveNullAndEmptyArrays: true } },

                    {
                        $lookup: {
                            from: "constituencies",
                            localField: "admin_level.constituency",
                            foreignField: "_id",
                            as: "admin_level.constituency"
                        }
                    },
                    { $unwind: { path: "$admin_level.constituency", preserveNullAndEmptyArrays: true } },

                    {
                        $lookup: {
                            from: "wards",
                            localField: "admin_level.ward",
                            foreignField: "_id",
                            as: "admin_level.ward"
                        }
                    },
                    { $unwind: { path: "$admin_level.ward", preserveNullAndEmptyArrays: true } },

                    // Add full_name for search if needed
                    ...(needsFullName ? [{
                        $addFields: {
                            full_name: {
                                $concat: [
                                    { $ifNull: ["$first_name", ""] },
                                    " ",
                                    { $ifNull: ["$last_name", ""] }
                                ]
                            }
                        }
                    }] : []),

                    // Apply search
                    ...(searchRegexMatch ? [searchRegexMatch] : []),

                    // Output shape
                    {
                        $project: {
                            first_name: 1,
                            last_name: 1,
                            full_name: 1,
                            "agent.name": 1,
                            "agent.username": 1,
                            gender: 1,
                            level: 1,
                            id_number: 1,
                            documents: 1,
                            contact_number: 1,
                            source: 1,
                            age_group: 1,
                            category: 1,
                            support_level: 1,
                            voter_card: 1,
                            createdAt: 1,
                            polling_station: {
                                _id: "$polling_station._id",
                                name: "$polling_station.name"
                            },
                            admin_level: {
                                provincial: {
                                    _id: "$admin_level.provincial._id",
                                    name: "$admin_level.provincial.name"
                                },
                                district: {
                                    _id: "$admin_level.district._id",
                                    name: "$admin_level.district.name"
                                },
                                constituency: {
                                    _id: "$admin_level.constituency._id",
                                    name: "$admin_level.constituency.name"
                                },
                                ward: {
                                    _id: "$admin_level.ward._id",
                                    name: "$admin_level.ward.name"
                                }
                            }
                        }
                    }
                ],
                totalCount: [
                    // Note: This only returns total documents (not filtered). Add same $lookup + $match if filtered count is needed
                    { $count: "total" }
                ]
            }
        }
    ];
};
